import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSessionPersistence } from '../../hooks/useSessionPersistence';
import { ResponsiveContainer, ResponsiveHeader, ResponsiveContent, ResponsiveFooter } from '../ui/ResponsiveLayout';

interface FuneralPreferences {
  serviceType: 'traditional' | 'memorial' | 'celebration' | 'none' | '';
  location: 'funeral_home' | 'religious' | 'home' | 'outdoor' | 'other' | '';
  customLocation: string;
  burialType: 'burial' | 'cremation' | 'donation' | 'other' | '';
  burialTypeDetails: string;
  religiousService: boolean;
  religiousDetails: string;
  musicPreferences: string;
  readings: string;
  specialRequests: string;
}

const Frame221: React.FC = () => {
  const navigate = useNavigate();
  const { session, updateTaskProgress, completeTask } = useSessionPersistence();

  const [preferences, setPreferences] = useState<FuneralPreferences>({
    serviceType: '',
    location: '',
    customLocation: '',
    burialType: '',
    burialTypeDetails: '',
    religiousService: false,
    religiousDetails: '',
    musicPreferences: '',
    readings: '',
    specialRequests: ''
  });

  // Helper function to save data directly to localStorage
  const saveToLocalStorage = (preferencesToSave: FuneralPreferences) => {
    try {
      const dataToSave = {
        answers: {
          funeralPreferences: preferencesToSave
        },
        lastUpdated: new Date().toISOString()
      };
      
      // Save to backup location for redundancy
      localStorage.setItem('funeral-preferences-backup', JSON.stringify(preferencesToSave));
      localStorage.setItem('funeral-data-backup', JSON.stringify(dataToSave));
      
      return true;
    } catch (error) {
      console.error('[Frame221] Direct save failed:', error);
      return false;
    }
  };

  // Load saved data on component mount - OPTIMIZED FOR CROSS-DEVICE SYNC
  useEffect(() => {
    // Priority 1: Load from session (authoritative source for cross-device sync)
    if (session?.taskProgress?.funeral?.answers) {
      const savedData = session.taskProgress.funeral.answers.funeralPreferences;
      if (savedData) {
        console.log('[Frame221] Loading from session:', savedData);
        setPreferences(savedData);
        return; // Session data takes priority
      }
    }

    // Priority 2: Fall back to localStorage (for same-device persistence)
    try {
      const backupData = localStorage.getItem('funeral-preferences-backup');
      if (backupData) {
        const data = JSON.parse(backupData);
        if (data) {
          console.log('[Frame221] Loading from localStorage backup:', data);
          setPreferences(data);
        }
      }
    } catch (error) {
      console.error('[Frame221] Failed to load from backup:', error);
    }
  }, [session?.taskProgress?.funeral]);

  const hasMeaningfulData = (prefs: FuneralPreferences): boolean => {
    // Check if any required fields are filled
    return !!(
      prefs.serviceType ||
      prefs.location ||
      prefs.burialType
    );
  };

  const handleInputChange = (field: keyof FuneralPreferences, value: string | boolean) => {
    const newPreferences = {
      ...preferences,
      [field]: value
    };

    setPreferences(newPreferences);

    // IMMEDIATE DIRECT SAVE
    saveToLocalStorage(newPreferences);

    // Also save to session (but don't wait for it)
    updateTaskProgress('funeral', {
      funeralPreferences: newPreferences
    }).then(() => {
      // Auto-complete if meaningful data exists
      if (hasMeaningfulData(newPreferences)) {
        completeTask('funeral');
        console.log('[Frame221] Task marked complete - funeral preferences filled');
      }
    }).catch(error => {
      console.error('[Frame221] Session save failed:', error);
    });
  };

  const handleNext = () => {
    // IMMEDIATE DIRECT SAVE
    saveToLocalStorage(preferences);

    // Also save to session
    updateTaskProgress('funeral', {
      funeralPreferences: preferences
    }).catch(error => {
      console.error('[Frame221] Session save failed on next:', error);
    });

    navigate('/frame-222');
  };

  const handleBack = () => {
    // IMMEDIATE DIRECT SAVE
    saveToLocalStorage(preferences);

    // Also save to session
    updateTaskProgress('funeral', {
      funeralPreferences: preferences
    }).catch(error => {
      console.error('[Frame221] Session save failed on back:', error);
    });

    navigate('/frame-219');
  };

  return (
    <ResponsiveContainer>
      <ResponsiveContent>
        <ResponsiveHeader
          title={`Your Funeral and Memorial Service Preferences`}
        />
        
        <div className="max-w-4xl">
          <div className="text-stone-800 text-lg font-medium font-sans mb-8">
            Help us understand your wishes for your funeral or memorial service
          </div>
          
          <div className="space-y-8">
            {/* Service Type */}
            <div>
              <label className="block text-stone-800 text-base font-medium font-sans mb-4">
                What type of service would you prefer?
              </label>
              <div className="space-y-3">
                {[
                  { value: 'traditional', label: 'Traditional funeral service' },
                  { value: 'memorial', label: 'Memorial service (without body present)' },
                  { value: 'celebration', label: 'Celebration of life' },
                  { value: 'none', label: 'No formal service' }
                ].map((option) => (
                  <label key={option.value} className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="radio"
                      name="serviceType"
                      value={option.value}
                      checked={preferences.serviceType === option.value}
                      onChange={(e) => handleInputChange('serviceType', e.target.value)}
                      className="w-4 h-4 text-cyan-700 focus:ring-cyan-700"
                    />
                    <span className="text-stone-700 text-base font-normal font-sans">{option.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Location */}
            <div>
              <label className="block text-stone-800 text-base font-medium font-sans mb-4">
                Where would you like the service to be held?
              </label>
              <div className="space-y-3">
                {[
                  { value: 'funeral_home', label: 'Funeral home' },
                  { value: 'religious', label: 'Religious institution (church, synagogue, etc.)' },
                  { value: 'home', label: 'Family home' },
                  { value: 'outdoor', label: 'Outdoor location' },
                  { value: 'other', label: 'Other location' }
                ].map((option) => (
                  <label key={option.value} className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="radio"
                      name="location"
                      value={option.value}
                      checked={preferences.location === option.value}
                      onChange={(e) => handleInputChange('location', e.target.value)}
                      className="w-4 h-4 text-cyan-700 focus:ring-cyan-700"
                    />
                    <span className="text-stone-700 text-base font-normal font-sans">{option.label}</span>
                  </label>
                ))}
              </div>
              
              {preferences.location === 'other' && (
                <div className="mt-4">
                  <input
                    type="text"
                    placeholder="Please specify the location..."
                    value={preferences.customLocation}
                    onChange={(e) => handleInputChange('customLocation', e.target.value)}
                    className="w-full px-4 py-3 border border-neutral-200 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-700 focus:border-transparent"
                  />
                </div>
              )}
            </div>

            {/* Burial Type */}
            <div>
              <label className="block text-stone-800 text-base font-medium font-sans mb-4">
                What are your wishes for your remains?
              </label>
              <div className="space-y-3">
                {[
                  { value: 'burial', label: 'Traditional burial' },
                  { value: 'cremation', label: 'Cremation' },
                  { value: 'donation', label: 'Body donation to science' },
                  { value: 'other', label: 'Other/Alternative' }
                ].map((option) => (
                  <label key={option.value} className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="radio"
                      name="burialType"
                      value={option.value}
                      checked={preferences.burialType === option.value}
                      onChange={(e) => handleInputChange('burialType', e.target.value)}
                      className="w-4 h-4 text-cyan-700 focus:ring-cyan-700"
                    />
                    <span className="text-stone-700 text-base font-normal font-sans">{option.label}</span>
                  </label>
                ))}

                {preferences.burialType === 'other' && (
                  <div className="mt-4">
                    <label
                      htmlFor="burialTypeDetails"
                      className="block text-stone-800 text-base font-medium font-sans mb-1"
                    >
                      Describe your preference (optional)
                    </label>
                    <textarea
                      id="burialTypeDetails"
                      placeholder="e.g., human composting, aquamation, green burial, or another option"
                      value={preferences.burialTypeDetails}
                      onChange={(e) => handleInputChange('burialTypeDetails', e.target.value)}
                      className="w-full px-4 py-3 border border-neutral-200 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-700 focus:border-transparent h-24 resize-none"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Religious Service */}
            <div>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={preferences.religiousService}
                  onChange={(e) => handleInputChange('religiousService', e.target.checked)}
                  className="w-4 h-4 text-cyan-700 focus:ring-cyan-700 rounded"
                />
                <span className="text-stone-800 text-base font-medium font-sans">Include religious or spiritual elements</span>
              </label>
              
              {preferences.religiousService && (
                <div className="mt-4">
                  <textarea
                    placeholder="Please describe any specific religious or spiritual preferences..."
                    value={preferences.religiousDetails}
                    onChange={(e) => handleInputChange('religiousDetails', e.target.value)}
                    className="w-full px-4 py-3 border border-neutral-200 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-700 focus:border-transparent h-24 resize-none"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </ResponsiveContent>
      
      <ResponsiveFooter
        title="Plan for Your Funeral and Memorial"
        subtitle="Choose your service and burial preferences"
        progressPercent={33}
        onBack={handleBack}
        onNext={handleNext}
        nextText="Next"
        backText="Back"
      />
    </ResponsiveContainer>
  );
};

export default Frame221;